// ── ADMIN: EMAIL LOG (dedicated view, full access only) ──────────────
// emailStatusPillClass is exported for admin-dashboard.js's real import
// (VIEWS['a-home']'s five-row email preview uses it too).
//
// elGoToPage/elSetType/elSetStatus exist only because the original inline
// handlers did bare assignment directly in the onclick/onchange attribute
// (e.g. onclick="elPage=5;elUpdate()") rather than calling a function. That
// works in a classic script, where the assignment reaches the real global —
// but elPage/elType/elStatus are now module-scoped `let`s, and an inline
// handler's bare `elPage = 5` would silently create a stray window property
// instead of touching this module's real binding (same class of problem
// core/state.js's setters and admin-students.js's setStProgram solve
// elsewhere in this migration, just triggered from a template string here
// instead of another file). Converting those three sites to real setter
// calls is the minimal fix — same net behavior, just reachable across the
// module boundary.
//
// Search/type/status filters and the "clear search" button live in the
// static outer template, wired via VIEW_INIT. The pagination buttons live
// inside elUpdate()'s own #elPager rewrite (called on every filter/page
// change, not just the initial route() render), so their listeners are
// attached directly inside elUpdate() itself — same self-healing pattern as
// admin-billing.js's updateReports()/wireCardButtons().
import { DB, session } from '../core/state.js';
import { fmtD } from '../core/format.js';
import { acctName, emailTo, canEdit } from '../core/domain-utils.js';
import { VIEWS, VIEW_INIT, route } from '../core/routing.js';

let elSearch = '';
let elType = '';
let elStatus = '';
let elPage = 1;
const EL_PER_PAGE = 25;

function elFilteredRows() {
  const q = elSearch.trim().toLowerCase();
  return DB.emailLog.slice().sort((a,b) => b.date.localeCompare(a.date)).filter(e => {
    if (elType && e.type !== elType) return false;
    if (elStatus && e.status !== elStatus) return false;
    if (q) {
      const recipient = e.accountId ? acctName(e.accountId) : (e.toEmail || '');
      const hay = (e.subject + ' ' + recipient + ' ' + emailTo(e)).toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

function elTypeLabel(t) { return t.replace(/_/g,' ').replace(/^./, c => c.toUpperCase()); }
// 'bounced'/'complained' need the same attention color as 'failed' -- a
// Resend delivery-issue webhook updates a log row to one of those after the
// fact, so this can't just be a static 'sent' vs 'failed' binary.
export function emailStatusPillClass(status) {
  if (status === 'failed' || status === 'bounced' || status === 'complained') return 'pill-due';
  if (status === 'scheduled') return 'pill-off';
  // Withheld on purpose (a feature running with its sends switched off), not a
  // delivery problem -- so grey, never the attention color.
  if (status === 'suppressed') return 'pill-off';
  return 'pill-paid';
}

function elSetSearch(val) {
  elSearch = val;
  const btn = document.getElementById('elClearBtn');
  if (btn) btn.style.display = val ? 'block' : 'none';
  elUpdate(true);
}
function elClearSearch() {
  elSearch = '';
  const inp = document.getElementById('elSearchInp');
  if (inp) { inp.value = ''; inp.focus(); }
  const btn = document.getElementById('elClearBtn');
  if (btn) btn.style.display = 'none';
  elUpdate(true);
}
function elSetType(val) { elType = val; elUpdate(true); }
function elSetStatus(val) { elStatus = val; elUpdate(true); }
function elGoToPage(page) { elPage = page; elUpdate(); }

function elUpdate(resetPage) {
  if (resetPage) elPage = 1;
  const rows = elFilteredRows();
  const totalPages = Math.max(1, Math.ceil(rows.length / EL_PER_PAGE));
  if (elPage > totalPages) elPage = totalPages;
  const start = (elPage - 1) * EL_PER_PAGE;
  const pageRows = rows.slice(start, start + EL_PER_PAGE);

  const body = document.getElementById('elTableWrap');
  if (!body) return;
  body.innerHTML = rows.length ? `<div class="tbl-wrap"><table>
      <tr><th>Date</th><th>Type</th><th>Account</th><th>Email</th><th>Subject</th><th>Status</th></tr>
      ${pageRows.map(e => `<tr>
        <td style="white-space:nowrap">${fmtD(e.date)}</td>
        <td><span class="pill pill-off" style="font-size:10px">${e.type.replace(/_/g,' ')}</span></td>
        <td>${e.accountId ? esc(acctName(e.accountId)) : '—'}</td>
        <td style="font-size:12px;color:var(--muted)">${esc(emailTo(e))}</td>
        <td style="font-size:13px">${esc(e.subject)}</td>
        <td><span class="pill ${emailStatusPillClass(e.status)}">${e.status}</span></td>
      </tr>`).join('')}
    </table></div>` : '<p class="empty">No emails match these filters.</p>';

  const footer = document.getElementById('elPager');
  if (footer) {
    if (rows.length) {
      const from = start + 1, to = Math.min(start + EL_PER_PAGE, rows.length);
      footer.innerHTML = `
        <span style="font-size:12px;color:var(--muted)">Showing ${from}–${to} of ${rows.length}</span>
        ${totalPages > 1 ? `<div style="display:flex;gap:4px;align-items:center">${elPageButtons(totalPages)}</div>` : ''}`;
    } else footer.innerHTML = '';
    footer.querySelectorAll('[data-action="goto-page"]').forEach(btn => {
      btn.addEventListener('click', () => elGoToPage(Number(btn.dataset.page)));
    });
  }
}

function elPageButtons(totalPages) {
  const btn = (label, page, opts = {}) => {
    const { disabled, active } = opts;
    if (disabled) return `<span style="padding:5px 9px;font-size:12px;color:var(--border-soft)">${label}</span>`;
    if (label === '…') return `<span style="padding:5px 4px;font-size:12px;color:var(--muted)">…</span>`;
    return `<button data-action="goto-page" data-page="${page}" style="padding:5px 10px;font-size:12px;border-radius:6px;cursor:pointer;font-family:inherit;border:1px solid ${active ? 'var(--gold)' : 'var(--border-soft)'};background:${active ? 'rgba(201,168,76,0.12)' : 'transparent'};color:${active ? 'var(--gold)' : 'var(--cream)'}">${label}</button>`;
  };
  // windowed page numbers with ellipses
  const pages = [];
  const around = 1;
  for (let p = 1; p <= totalPages; p++) {
    if (p === 1 || p === totalPages || (p >= elPage - around && p <= elPage + around)) pages.push(p);
    else if (pages[pages.length - 1] !== '…') pages.push('…');
  }
  return [
    btn('‹', elPage - 1, { disabled: elPage === 1 }),
    ...pages.map(p => p === '…' ? btn('…') : btn(String(p), p, { active: p === elPage })),
    btn('›', elPage + 1, { disabled: elPage === totalPages })
  ].join('');
}

VIEWS['a-email-log'] = () => {
  if (!canEdit(session)) return '<p class="empty">You don\'t have access to the email log.</p>';
  elSearch = ''; elType = ''; elStatus = ''; elPage = 1;
  const types = [...new Set(DB.emailLog.map(e => e.type))].sort();
  const statuses = [...new Set(DB.emailLog.map(e => e.status))].sort();
  const html = `
    <div class="page-head">
      <span class="eyebrow check-row" onclick="route('a-home')">‹ Dashboard</span>
      <h2 class="serif">Email <em>Log</em></h2>
    </div>
    <div class="card">
      <div class="filters" style="margin-bottom:16px;flex-wrap:wrap">
        <div class="field" style="margin-bottom:0;flex:1;min-width:200px">
          <label>Search</label>
          <div style="position:relative">
            <input type="text" id="elSearchInp" placeholder="Subject, recipient, or email" style="padding-right:28px;width:100%;box-sizing:border-box">
            <button id="elClearBtn" aria-label="Clear search" style="position:absolute;right:6px;top:50%;transform:translateY(-50%);background:none;border:none;cursor:pointer;color:var(--muted);font-size:18px;line-height:1;padding:2px 4px;display:none">×</button>
          </div>
        </div>
        <div class="field" style="margin-bottom:0">
          <label>Type</label>
          <select id="elTypeSel">
            <option value="">All types</option>
            ${types.map(t => `<option value="${esc(t)}">${esc(elTypeLabel(t))}</option>`).join('')}
          </select>
        </div>
        <div class="field" style="margin-bottom:0">
          <label>Status</label>
          <select id="elStatusSel">
            <option value="">All statuses</option>
            ${statuses.map(s => `<option value="${esc(s)}">${esc(s.replace(/^./, c => c.toUpperCase()))}</option>`).join('')}
          </select>
        </div>
      </div>
      <div id="elTableWrap"></div>
      <div id="elPager" style="display:flex;justify-content:space-between;align-items:center;margin-top:16px"></div>
    </div>`;
  setTimeout(() => elUpdate(false), 0);
  return html;
};

VIEW_INIT['a-email-log'] = () => {
  const searchInp = document.getElementById('elSearchInp');
  if (searchInp) searchInp.addEventListener('input', () => elSetSearch(searchInp.value));
  const clearBtn = document.getElementById('elClearBtn');
  if (clearBtn) clearBtn.addEventListener('click', elClearSearch);
  const typeSel = document.getElementById('elTypeSel');
  if (typeSel) typeSel.addEventListener('change', () => elSetType(typeSel.value));
  const statusSel = document.getElementById('elStatusSel');
  if (statusSel) statusSel.addEventListener('change', () => elSetStatus(statusSel.value));
};
